import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../config/api';
import { useGlobalWebSocket } from './GlobalWebSocketContext';

const UnreadMessagesContext = createContext();

export const useUnreadMessagesContext = () => {
  const context = useContext(UnreadMessagesContext);
  if (!context) {
    throw new Error('useUnreadMessagesContext must be used within an UnreadMessagesProvider');
  }
  return context;
};

export const UnreadMessagesProvider = ({ children }) => {
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(false);
  
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const subscribedTripsRef = useRef(new Set());
  const activeSubscribersRef = useRef(new Map()); // subscriberId -> Set of tripIds
  const initialFetchDoneRef = useRef(new Set()); // Track which trips have been initially fetched
  
  const globalWebSocket = useGlobalWebSocket();
  const hasWebSocketContext = Boolean(globalWebSocket);

  // ONE-TIME fetch for initial unread counts (NO POLLING)
  const fetchInitialUnreadCounts = useCallback(async (tripIds) => {
    if (!isMountedRef.current || tripIds.length === 0) {
      return;
    }
    
    // Only fetch trips that haven't been fetched yet
    const tripsToFetch = tripIds.filter(tripId => !initialFetchDoneRef.current.has(tripId));
    
    if (tripsToFetch.length === 0) {
      return; // All trips already fetched
    }
    
    console.log('📊 [UnreadMessages] Initial fetch for trips:', tripsToFetch);
    
    try {
      setLoading(true);
      
      // Cancel previous request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Fetch all trips in parallel (no batching delay needed for initial fetch)
      const promises = tripsToFetch.map(async (tripId) => {
        try {
          if (signal.aborted) {
            return { tripId, unreadCount: 0 };
          }
          
          // Validate trip ID before making API call
          if (!tripId || isNaN(tripId) || tripId === 'NaN') {
            console.warn('🚫 [UnreadMessages] Skipping invalid trip ID:', tripId);
            return { tripId, unreadCount: 0 };
          }
          
          const response = await api.get(`/trips/${tripId}/messages`, { signal });
          const messages = response.data;
          
          const lastRead = localStorage.getItem(`lastReadMessage_${tripId}`);
          const lastReadId = lastRead ? parseInt(lastRead, 10) : 0;
          
          const unreadCount = messages.filter(msg => msg.id > lastReadId).length;
          
          // Mark this trip as fetched
          initialFetchDoneRef.current.add(tripId);
          
          return { tripId, unreadCount };
        } catch (error) {
          if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
            console.error(`Error fetching initial messages for trip ${tripId}:`, error);
          }
          return { tripId, unreadCount: 0 };
        }
      });

      const results = await Promise.all(promises);
      
      if (!signal.aborted && isMountedRef.current) {
        const counts = {};
        results.forEach(({ tripId, unreadCount }) => {
          counts[tripId] = unreadCount;
        });
        
        setUnreadCounts(prev => ({ ...prev, ...counts }));
        console.log('✅ [UnreadMessages] Initial fetch complete:', counts);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED' && isMountedRef.current) {
        console.error('Error in initial fetch:', error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Subscribe to trip IDs (NO POLLING - WebSocket only)
  const subscribeToTrips = useCallback((tripIds, subscriberId) => {
    console.log('📝 [UnreadMessages] Subscribe request:', { tripIds, subscriberId });
    
    // Update subscriber tracking
    activeSubscribersRef.current.set(subscriberId, new Set(tripIds));
    
    // Find truly new trip IDs
    const newTripIds = tripIds.filter(id => !subscribedTripsRef.current.has(id));
    
    // Add to subscribed set
    newTripIds.forEach(tripId => subscribedTripsRef.current.add(tripId));
    
    if (newTripIds.length > 0) {
      console.log('🔔 [UnreadMessages] New subscriptions (WebSocket-only):', newTripIds);
      
      // ONE-TIME initial fetch for new trips
      fetchInitialUnreadCounts(newTripIds);
      
      // Subscribe to WebSocket for real-time updates (NO POLLING)
      if (hasWebSocketContext && globalWebSocket) {
        newTripIds.forEach(tripId => {
          globalWebSocket.subscribeToTrip(tripId);
        });
      } else {
        console.warn('⚠️ [UnreadMessages] No WebSocket available - data will not update in real-time');
      }
    }
  }, [hasWebSocketContext, globalWebSocket, fetchInitialUnreadCounts]);

  // Unsubscribe from trip IDs
  const unsubscribeFromTrips = useCallback((tripIds, subscriberId) => {
    console.log('📝 [UnreadMessages] Unsubscribe request:', { tripIds, subscriberId });
    
    // Remove subscriber
    activeSubscribersRef.current.delete(subscriberId);
    
    // Find trips that are no longer needed by any subscriber
    const stillNeededTripIds = new Set();
    activeSubscribersRef.current.forEach(subscriberTripIds => {
      subscriberTripIds.forEach(tripId => stillNeededTripIds.add(tripId));
    });
    
    // Unsubscribe from trips that are no longer needed
    const tripsToUnsubscribe = tripIds.filter(tripId => !stillNeededTripIds.has(tripId));
    
    if (tripsToUnsubscribe.length > 0) {
      console.log('🔕 [UnreadMessages] Removing unused subscriptions:', tripsToUnsubscribe);
      
      tripsToUnsubscribe.forEach(tripId => {
        subscribedTripsRef.current.delete(tripId);
        
        if (hasWebSocketContext && globalWebSocket) {
          globalWebSocket.unsubscribeFromTrip(tripId);
        }
      });
    }
  }, [hasWebSocketContext, globalWebSocket]);

  // Mark messages as read
  const markAsRead = useCallback((tripId, messageId) => {
    if (messageId && isMountedRef.current) {
      localStorage.setItem(`lastReadMessage_${tripId}`, messageId.toString());
      setUnreadCounts(prev => {
        const newCounts = { ...prev, [tripId]: 0 };
        console.log('📖 [UnreadMessages] Marked as read:', { tripId, messageId, newCounts });
        return newCounts;
      });
    }
  }, []);

  // Manual refresh function (for user-initiated refresh only)
  const manualRefresh = useCallback(async () => {
    console.log('🔄 [UnreadMessages] Manual refresh requested');
    
    // Clear initial fetch tracking to allow re-fetch
    initialFetchDoneRef.current.clear();
    
    // Re-fetch all currently subscribed trips
    const currentTrips = Array.from(subscribedTripsRef.current);
    if (currentTrips.length > 0) {
      await fetchInitialUnreadCounts(currentTrips);
    }
  }, [fetchInitialUnreadCounts]);

  // Set up WebSocket message listener for REAL-TIME updates (NO POLLING)
  useEffect(() => {
    if (!hasWebSocketContext || !globalWebSocket) {
      console.log('⚠️ [UnreadMessages] No WebSocket - real-time updates disabled');
      return;
    }

    console.log('🌐 [UnreadMessages] Setting up WebSocket listener (real-time only)');

    const unsubscribeMessage = globalWebSocket.addEventListener('message', (data) => {
      console.log('🌐 [UnreadMessages] WebSocket message received:', {
        type: data.type,
        hasMessage: !!data.message,
        tripId: data.message?.trip_id,
        messageId: data.message?.id,
        isSubscribed: data.message ? subscribedTripsRef.current.has(data.message.trip_id) : false,
        data
      });
      if (data.message && (subscribedTripsRef.current.has(data.message.trip_id) || subscribedTripsRef.current.has(data.message.trip_id.toString()))) {
        const tripId = data.message.trip_id;
        const messageId = data.message.id;
        // If user is viewing the chat tab specifically, don't count as unread
        // Check both URL parameters and default tab behavior
        const isOnTripPage = window.location.pathname.includes(`/trips/${tripId}`);
        const isChatTabActive = window.location.search.includes('tab=chat') || 
                               window.location.hash.includes('chat');
        
        if (document.visibilityState === 'visible' && isOnTripPage && isChatTabActive) {
          console.log('📨 [UnreadMessages] Message received while actively viewing chat tab - not counting as unread:', {
            tripId,
            messageId,
            pathname: window.location.pathname,
            search: window.location.search,
            data: data.message
          });
          return;
        }
        const lastRead = localStorage.getItem(`lastReadMessage_${tripId}`);
        const lastReadId = lastRead ? parseInt(lastRead, 10) : 0;
        if (messageId > lastReadId) {
          setUnreadCounts(prev => {
            const newCounts = { ...prev, [tripId]: (prev[tripId] || 0) + 1 };
            console.log('📨 [UnreadMessages] Real-time update:', {
              tripId,
              messageId,
              lastReadId,
              newCounts
            });
            return newCounts;
          });
        } else {
          console.log('📨 [UnreadMessages] Message already read or not newer:', { tripId, messageId, lastReadId });
        }
      } else {
        console.log('📨 [UnreadMessages] Message ignored (not subscribed or missing tripId):', data);
      }
    });

    return () => {
      console.log('🔌 [UnreadMessages] Removing WebSocket listener');
      unsubscribeMessage();
    };
  }, [hasWebSocketContext, globalWebSocket]);

  // Handle visibility change - manual refresh only when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && subscribedTripsRef.current.size > 0 && isMountedRef.current) {
        if (!hasWebSocketContext || !globalWebSocket?.connected) {
          console.log('🔄 [UnreadMessages] Tab visible + no WebSocket - manual refresh');
          manualRefresh();
        } else {
          console.log('✅ [UnreadMessages] Tab visible + WebSocket connected - no refresh needed');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasWebSocketContext, globalWebSocket?.connected, manualRefresh]);

  // Cleanup on unmount - separate from WebSocket management
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      console.log('🧹 [UnreadMessages] Context cleanup - NO POLLING TO STOP');
      isMountedRef.current = false;
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // WebSocket subscriptions are managed by individual useUnreadMessages hook instances
  // Each hook properly unsubscribes when it unmounts via unsubscribeFromTrips
  // No global cleanup needed - this prevents race conditions and ref capture issues

  const value = {
    unreadCounts,
    loading,
    subscribeToTrips,
    unsubscribeFromTrips,
    markAsRead,
    refresh: manualRefresh, // Manual refresh only - NO automatic polling
    connected: hasWebSocketContext ? globalWebSocket?.connected : false
  };

  return (
    <UnreadMessagesContext.Provider value={value}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};
